import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, Signal, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DeletedEntity } from '@app/_interfaces/feature.interface';
import { HabitSetup } from '@app/_interfaces/habit.interface';
import { AlertService } from '@app/_services/alert.service';
import { CacheService } from '@app/_services/cache.service';
import { DependencyService } from '@app/_services/dependency.service';
import { SettingsService } from '@app/_services/settings.service';
import { UtilsService } from '@app/_services/utils.service';
import { HabitStore } from '@app/_stores';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { HabitEntriesComponent } from '../habit-entries/habit-entries.component';
import { HabitBlockComponent } from '../habit-block/habit-block.component';
import { HabitSetupFormComponent } from '../habit-setup-form/habit-setup-form.component';
import { ModalService } from '@app/_services/modal.service';

@Component({
    selector: 'app-habit-trash-tab',
    imports: [
        CommonModule,
        SvgComponent,
        HabitBlockComponent
    ],
    templateUrl: './habit-trash-tab.component.html',
    styleUrl: './habit-trash-tab.component.scss'
})

export class HabitTrashTabComponent {

  readonly habitStore = inject(HabitStore);
  deletedHabits: Signal<DeletedEntity[]> = computed(() => {
    const deletedEntities: DeletedEntity[] = [];
    this.habitStore.setups().forEach(habit => {
      if (habit.deletedAt && !habit.permaDeletedAt && habit.uid === this.cc.user.uid) {
        deletedEntities.push({
          entityName: 'habit',
          entityId: habit.id,
          deletedAt: this.utils.toDate(habit.deletedAt),
          entries: this.ds.getAllDatesOfEntity(habit, 'habit', this.cc.user.uid),
          entityEntry: this.habitStore.getHabitEntry(habit.id),
          isRepeated: true
        });
      }
    });

    deletedEntities.sort((a, b) => {
      return b.deletedAt.getTime() - a.deletedAt.getTime();
    });
    return deletedEntities;
  });
  // Show ALL metadata in trash screen (description is controlled by descriptionType, not show array)
  show: WritableSignal<string[]> = signal(['label', 'time', 'reminder', 'duration', 'attachments', 'repeat', 'habitResponse', 'collaboratorsCount', 'hashtag', 'deletedAt']);

  idToSetup: Signal<Record<string, HabitSetup>> = this.habitStore.idToSetup;

  constructor(
    public ss: SettingsService,
    private alertService: AlertService,
    public dialog: MatDialog,
    public cc: CacheService,
    public ds: DependencyService,
    private utils: UtilsService,
    private modalService: ModalService
  ) { }

  trackByEntity(index: number, entity: DeletedEntity) {
    return entity.entityId;
  };

  openSetup(entityId: string) {
    const setup = this.idToSetup()[entityId];

    const data = {
        mode: 'edit',
        value: setup,
        habitType: setup.habitType
      }

      if (this.cc.isNarrowScreen()) {
        const setupDialog = this.modalService.openMainContent(
          HabitSetupFormComponent,
          data,
          { disableClose: true, autoFocus: false }
        );
      } else {
        const setupDialog = this.modalService.openLevel1(
          HabitSetupFormComponent,
          data,
          { disableClose: true, autoFocus: false }
        );
      }
  };

  openEntities(entityId: string) {
    this.modalService.openLevel2(
      HabitEntriesComponent,
      { entityId: entityId },
      { disableClose: false, autoFocus: false }
    );
  };

  async remove(entityId: string) {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('removeConfirmation_title'),
      description: this.ss.t('removeConfirmation_content'),
      yesButtonText: this.ss.t('common_buttonRemove'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const habit = this.idToSetup()[entityId];
    habit.permaDeletedAt = new Date();
    this.habitStore.updateSetups([habit]);
  };

  async restore(entityId: string) {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('restoreConfirmation_title'),
      description: this.ss.t('restoreConfirmation_content'),
      yesButtonText: this.ss.t('common_restore'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-35',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const habit = this.idToSetup()[entityId];
    habit.deletedAt = null;
    this.habitStore.updateSetups([habit]);
    const isShow = await this.alertService.toast('habitSetupRestored_content', 'common_show', 'common_dismiss');
    if (isShow) {
      this.openSetup(entityId);
    }
  };
}
